import { Check, Copy } from 'feather-icons-react';
import { useState } from 'react';
import { copyToClipboard } from 'utils';

type CodeBlockProps = {
  label?: string;
  language?: 'js' | 'bash' | 'html';
  code: string;
  /** Cap the rendered height; users can scroll. Defaults to ~280px. */
  maxHeight?: string;
};

/**
 * Snippet block with a copy-to-clipboard button. Shared by the integration setup steps
 * and the per-rule snippets.
 */
export const CodeBlock = ({
  label,
  language,
  code,
  maxHeight = 'max-h-[280px]',
}: CodeBlockProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    try {
      copyToClipboard(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy snippet:', err);
    }
  };

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-primary-gray-500">
          {label ?? language ?? ''}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium text-primary-gray-600 hover:bg-primary-gray-100 hover:text-primary-gray-900"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre
        className={`overflow-auto whitespace-pre rounded-md bg-primary-gray-900 px-3 py-2 font-mono text-[11px] leading-relaxed text-primary-gray-50 ${maxHeight}`}
      >
        {code}
      </pre>
    </div>
  );
};
